import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const futureTopics = [
  {
    id: 'bci',
    icon: 'ri-mental-health-line',
    title: '脑机接口',
    subtitle: 'Brain-Computer Interface',
    tag: '2028-2032',
    tagColor: '#A855F7',
    splatColor: 'rgba(168,85,247,0.15)',
    headline: '意念操控元宇宙，思维即是命令',
    episode: 'EP.01',
    storyTitle: '《意念之城》',
    story: '2031年，上海。林晓雨戴上薄如蝉翼的BCI贴片，闭上眼睛——她的意识瞬间穿越进入"镜城"元宇宙。她只需"想"，就能在虚空中绘制建筑蓝图；她只需"感受"，就能与千里之外的同事共享同一片数字天空。那天，她用意念完成了一座跨越虚实的桥梁设计，而她的身体，从未离开过那把椅子。这不是科幻，这是2031年的日常。',
    desc: '脑机接口（BCI）技术将彻底颠覆人机交互范式。通过非侵入式神经信号采集，用户仅凭意念即可在元宇宙中移动、创作、交流。META66正与全球顶尖神经科学团队合作，推动BCI技术从实验室走向大众消费市场。',
    milestones: [
      { year: '2026', event: '非侵入式BCI设备消费级原型发布，延迟<100ms' },
      { year: '2028', event: '意念输入延迟降至50ms，支持基础元宇宙操控' },
      { year: '2030', event: 'BCI与XR设备深度集成，全感官元宇宙实现' },
      { year: '2032', event: '脑机接口大众普及，元宇宙成为第二现实' },
    ],
    img: 'https://public.readdy.ai/ai/img_res/724bb3a5606402779ee541e2c201d133.jpg',
    stats: [{ label: '信号精度', value: '99.7%' }, { label: '延迟', value: '50ms' }, { label: '通道数', value: '1024+' }],
    videoLabel: '观看：意念操控演示',
    videoUrl: 'https://www.youtube.com/watch?v=rsCul1sp4hQ',
    applications: ['医疗康复', '元宇宙交互', '远程操控', '意识存档'],
  },
  {
    id: 'starlink',
    icon: 'ri-global-line',
    title: '星链与卫星互联',
    subtitle: 'Starlink & Satellite Internet',
    tag: '2025-2030',
    tagColor: '#00FFCC',
    splatColor: 'rgba(0,255,204,0.12)',
    headline: '万颗卫星织网，地球无死角连接',
    episode: 'EP.02',
    storyTitle: '《最后一公里》',
    story: '2027年，西藏阿里高原，海拔5200米。牧民扎西打开手机，流畅地与北京的孙子视频通话，孙子正在元宇宙里上课，老师是一个来自哈佛的AI教授。扎西摇摇头笑了——他记得十年前，这里连2G信号都没有。如今，头顶的6000颗卫星让他和全球任何角落的人都处于同一张网络里。星链，让"数字鸿沟"成为历史。',
    desc: '以SpaceX星链为代表的低轨卫星互联网星座，正在构建覆盖全球每一寸土地的超高速网络。结合6G地面网络，将实现真正的"无处不在"连接，为元宇宙的全球普及奠定基础设施底座。',
    milestones: [
      { year: '2025', event: '全球卫星数量突破6000颗，覆盖率达95%' },
      { year: '2026', event: '卫星互联网延迟降至20ms，带宽突破1Gbps' },
      { year: '2028', event: '天地一体化网络成型，6G与卫星无缝切换' },
      { year: '2030', event: '全球100%覆盖，数字鸿沟彻底消除' },
    ],
    img: 'https://readdy.ai/api/search-image?query=starlink%20satellite%20constellation%20glowing%20cyan%20neon%20network%20earth%20orbit%20space%20internet%20connectivity%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic&width=700&height=420&seq=starlink-main-v4-001&orientation=landscape',
    stats: [{ label: '卫星数量', value: '6000+' }, { label: '延迟', value: '20ms' }, { label: '覆盖率', value: '95%' }],
    videoLabel: '观看：星链组网全景',
    videoUrl: 'https://www.youtube.com/watch?v=rnkl0oGBqoE',
    applications: ['偏远地区覆盖', '海洋航运', '航空互联', '灾难救援'],
  },
  {
    id: 'evtol',
    icon: 'ri-flight-takeoff-line',
    title: '低空经济 / eVTOL',
    subtitle: 'Low-Altitude Economy & eVTOL',
    tag: '2026-2035',
    tagColor: '#FF6B35',
    splatColor: 'rgba(255,107,53,0.12)',
    headline: '城市上空的第五维度，飞行出行新纪元',
    episode: 'EP.03',
    storyTitle: '《空中通勤》',
    story: '2030年，深圳湾。陈明打开手机APP，预约了一辆eVTOL飞行出租车。7分钟后，一架安静的电动飞行器降落在楼顶停机坪。他坐进去，系好安全带，飞行器自动规划航线，穿越深圳湾上空，15分钟后降落在香港科技园。地面上，同样的路程需要90分钟。低空经济，让城市的第三维度成为日常通勤的新选择。',
    desc: '电动垂直起降飞行器（eVTOL）与低空经济正在重塑城市交通格局。大湾区已率先布局低空航线网络，预计2030年前实现城市间30分钟飞行通勤。结合AI自动驾驶与5G/6G实时通信，低空经济将创造万亿级新市场。',
    milestones: [
      { year: '2026', event: '大湾区首批eVTOL商业航线开通，载客运营' },
      { year: '2028', event: '全国主要城市低空航线网络成型，千架规模' },
      { year: '2030', event: 'eVTOL自动驾驶普及，城市空中交通系统建立' },
      { year: '2035', event: '低空经济产值突破5万亿，成为新支柱产业' },
    ],
    img: 'https://readdy.ai/api/search-image?query=eVTOL%20electric%20flying%20taxi%20urban%20air%20mobility%20city%20skyline%20glowing%20orange%20neon%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic%20low%20altitude%20economy&width=700&height=420&seq=evtol-main-v4-001&orientation=landscape',
    stats: [{ label: '市场规模', value: '5万亿' }, { label: '通勤时间', value: '-80%' }, { label: '碳排放', value: '零排放' }],
    videoLabel: '观看：eVTOL飞行演示',
    videoUrl: 'https://www.youtube.com/watch?v=Hu9eSBBMFAE',
    applications: ['城市通勤', '货物配送', '医疗急救', '旅游观光'],
  },
  {
    id: 'print3d',
    icon: 'ri-printer-line',
    title: '3D打印制造',
    subtitle: '3D Printing & Additive Manufacturing',
    tag: '2025-2035',
    tagColor: '#FFD700',
    splatColor: 'rgba(255,215,0,0.12)',
    headline: '万物皆可打印，制造业的终极革命',
    episode: 'EP.04',
    storyTitle: '《打印一座房子》',
    story: '2029年，成都郊外。一台巨型3D打印机正在"打印"一栋三层别墅。72小时后，房子完工——墙体、管道、电路全部一体成型，造价仅为传统建筑的30%。屋主王芳在元宇宙里设计了这栋房子，AI优化了结构，机器人完成了施工。她从未踏上过这片土地，却亲手"建造"了自己的家。这就是2029年的建筑业。',
    desc: '3D打印技术正从原型制造走向大规模生产，覆盖建筑、医疗、航空、食品等全行业。生物3D打印可制造人体器官，金属3D打印颠覆航空制造，混凝土3D打印重塑建筑业。结合AI设计与元宇宙虚拟工厂，制造业将迎来彻底变革。',
    milestones: [
      { year: '2026', event: '生物3D打印肾脏完成首例人体移植手术' },
      { year: '2028', event: '3D打印建筑在全国主要城市规模化应用' },
      { year: '2030', event: '金属3D打印进入航空主结构件量产阶段' },
      { year: '2035', event: '个人3D打印机普及，家庭制造时代到来' },
    ],
    img: 'https://readdy.ai/api/search-image?query=3D%20printing%20futuristic%20manufacturing%20glowing%20yellow%20neon%20additive%20manufacturing%20layer%20by%20layer%20construction%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic%20technology&width=700&height=420&seq=print3d-main-v4-001&orientation=landscape',
    stats: [{ label: '成本降低', value: '70%' }, { label: '建造速度', value: '10x' }, { label: '材料利用', value: '99%' }],
    videoLabel: '观看：3D打印建筑全程',
    videoUrl: 'https://www.youtube.com/watch?v=GUdnrtnjT5Q',
    applications: ['建筑施工', '医疗器官', '航空零件', '食品制造'],
  },
  {
    id: 'spatial',
    icon: 'ri-map-2-line',
    title: '空间计算',
    subtitle: 'Spatial Computing',
    tag: '2025-2030',
    tagColor: '#00FF88',
    splatColor: 'rgba(0,255,136,0.12)',
    headline: '物理空间数字化，万物皆可交互',
    episode: 'EP.05',
    storyTitle: '《数字城市》',
    story: '2028年，北京。城市管理员李刚戴上AR眼镜，整座城市的数字孪生体浮现在他眼前——每一栋建筑、每一条管道、每一辆车都有实时数据标签。他发现朝阳区某处地下管道压力异常，手指一点，维修机器人已经出发。与此同时，一场演唱会的全息投影正在天安门广场上空绽放，数百万人在元宇宙里同步观看。物理与数字，已经无法分辨边界。',
    desc: '空间计算将数字内容精准锚定于物理世界的三维坐标，实现真正的虚实融合。结合6G超低延迟网络与AI实时感知，META66空间计算平台可将整座城市转化为可交互的数字孪生空间，开创全新的生活、工作与娱乐方式。',
    milestones: [
      { year: '2025', event: '城市级空间计算平台上线，覆盖深圳全域' },
      { year: '2027', event: '空间锚点精度达到毫米级，支持百万并发' },
      { year: '2028', event: '全国主要城市完成空间数字化建模' },
      { year: '2030', event: '全球空间互联网络建立，虚实边界消融' },
    ],
    img: 'https://readdy.ai/api/search-image?query=spatial%20computing%20augmented%20reality%20city%20digital%20overlay%20holographic%20information%20green%20neon%20dark%20background%20futuristic%20technology%20sci-fi%20cinematic%20wide%20shot%20ultra%20detailed%20artistic&width=700&height=420&seq=spatial-main-v4-001&orientation=landscape',
    stats: [{ label: '定位精度', value: '1mm' }, { label: '覆盖面积', value: '1000km²' }, { label: '并发用户', value: '1000万' }],
    videoLabel: '观看：数字孪生城市',
    videoUrl: 'https://www.youtube.com/watch?v=4QbIc49NKHE',
    applications: ['智慧城市', '工业制造', '医疗健康', '教育培训'],
  },
  {
    id: 'space',
    icon: 'ri-rocket-line',
    title: '太空旅游',
    subtitle: 'Space Tourism',
    tag: '2027-2040',
    tagColor: '#FF4081',
    splatColor: 'rgba(255,64,129,0.12)',
    headline: '星辰大海不再遥远，人人皆可触摸宇宙',
    episode: 'EP.06',
    storyTitle: '《轨道上的婚礼》',
    story: '2033年，国际空间站轨道高度400公里。张伟和李娜在太空中举行了人类历史上第一场"轨道婚礼"。地球在他们身后缓缓转动，蓝色的光晕映照着两人的脸。地面上，10亿人通过元宇宙全息直播同步见证了这一刻——仿佛自己也漂浮在星空之中。太空旅游，已经不再是亿万富翁的专属游戏。',
    desc: '随着SpaceX、蓝色起源、维珍银河等商业航天公司的竞争，太空旅游成本正在快速下降。预计2030年，亚轨道旅游票价将降至10万美元以内，轨道旅游将在2035年前实现商业化。太空旅游将与元宇宙深度融合，创造前所未有的体验经济。',
    milestones: [
      { year: '2027', event: '亚轨道旅游年旅客突破1万人次，票价降至20万' },
      { year: '2030', event: '商业空间站建成，轨道旅游开始商业运营' },
      { year: '2033', event: '月球旅游首班商业飞船发射，人类重返月球' },
      { year: '2040', event: '火星移民先遣队出发，人类成为多星球物种' },
    ],
    img: 'https://readdy.ai/api/search-image?query=space%20tourism%20rocket%20launch%20earth%20orbit%20glowing%20pink%20neon%20stars%20galaxy%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic%20commercial%20space%20travel&width=700&height=420&seq=space-main-v4-001&orientation=landscape',
    stats: [{ label: '票价降幅', value: '-90%' }, { label: '年旅客', value: '10万+' }, { label: '目的地', value: '月球/火星' }],
    videoLabel: '观看：太空旅游全程',
    videoUrl: 'https://www.youtube.com/watch?v=zA4S_OLbdHo',
    applications: ['太空旅游', '科学研究', '资源开采', '人类移民'],
  },
  {
    id: 'ai',
    icon: 'ri-brain-line',
    title: 'AI全面爆发',
    subtitle: 'AI Explosion & AGI',
    tag: '2025-2035',
    tagColor: '#FFD700',
    splatColor: 'rgba(255,215,0,0.12)',
    headline: '超级智能降临，人类文明的最大变量',
    episode: 'EP.07',
    storyTitle: '《最后一个程序员》',
    story: '2032年，硅谷。前程序员马克坐在咖啡馆里，看着窗外的世界。他的工作已经被AI取代了——不是因为他不够好，而是因为AI太好了。但他并不悲伤。他现在是一名"AI导演"，用自然语言指挥AI构建整个数字世界。他的团队只有他一个人，却创造了一个拥有1亿用户的元宇宙游戏。AI，让一个人拥有了一支军队的创造力。',
    desc: 'AI正在以指数级速度进化，从专用AI到通用AI（AGI）的跨越预计在2030年前后实现。AI将全面渗透医疗、教育、创作、科研、制造等所有领域，重塑人类的工作方式与创造力边界。META66AI研究院正在探索AI与元宇宙的深度融合路径。',
    milestones: [
      { year: '2026', event: 'AI在所有专业领域超越人类专家水平' },
      { year: '2028', event: 'AGI雏形出现，具备跨领域自主推理能力' },
      { year: '2030', event: 'AI自主科研，每年产出相当于人类百年的成果' },
      { year: '2035', event: 'ASI（超级智能）出现，人类进入后奇点时代' },
    ],
    img: 'https://readdy.ai/api/search-image?query=artificial%20general%20intelligence%20AGI%20neural%20network%20explosion%20glowing%20golden%20yellow%20neon%20brain%20digital%20mind%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic&width=700&height=420&seq=ai-main-v4-001&orientation=landscape',
    stats: [{ label: '算力增长', value: '100x/年' }, { label: '应用领域', value: '全行业' }, { label: '生产力提升', value: '1000x' }],
    videoLabel: '观看：AGI发展路线图',
    videoUrl: 'https://www.youtube.com/watch?v=AaTRHFaaPG8',
    applications: ['科学研究', '医疗诊断', '创意创作', '教育个性化'],
  },
  {
    id: 'robot',
    icon: 'ri-robot-line',
    title: '人形机器人',
    subtitle: 'Humanoid Robots',
    tag: '2026-2035',
    tagColor: '#00FFCC',
    splatColor: 'rgba(0,255,204,0.12)',
    headline: '钢铁伙伴走进生活，人机共存新文明',
    episode: 'EP.08',
    storyTitle: '《我的机器人室友》',
    story: '2031年，北京朝阳区。大学生小雨的公寓里多了一个室友——人形机器人"小蓝"。小蓝会做饭、会打扫、会辅导功课，还会在小雨失眠的夜晚陪她聊天。小蓝不需要睡觉，不会生气，永远耐心。小雨有时候会忘记小蓝是机器人——直到她看见小蓝充电时那双发光的眼睛。人形机器人，正在成为人类最亲密的伙伴。',
    desc: '以特斯拉Optimus、波士顿动力Atlas为代表的人形机器人正在快速走向实用化。预计2028年前，人形机器人将进入工厂、医院、家庭等场景，承担重复性、危险性工作。结合AI与元宇宙远程操控，人形机器人将成为人类在物理世界的"数字分身"。',
    milestones: [
      { year: '2026', event: '人形机器人进入工厂量产，替代危险岗位' },
      { year: '2028', event: '家用人形机器人上市，售价降至10万元以内' },
      { year: '2030', event: '人形机器人具备自主学习能力，适应复杂环境' },
      { year: '2035', event: '人形机器人数量超过人类，成为主要劳动力' },
    ],
    img: 'https://readdy.ai/api/search-image?query=humanoid%20robot%20futuristic%20glowing%20cyan%20neon%20artificial%20intelligence%20android%20walking%20city%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic%20technology&width=700&height=420&seq=robot-main-v4-001&orientation=landscape',
    stats: [{ label: '售价', value: '<10万' }, { label: '工作时长', value: '24/7' }, { label: '任务完成率', value: '99.5%' }],
    videoLabel: '观看：人形机器人演示',
    videoUrl: 'https://www.youtube.com/watch?v=cpraXaw7dyc',
    applications: ['工厂制造', '家庭服务', '医疗护理', '灾难救援'],
  },
  {
    id: 'autodrive',
    icon: 'ri-car-line',
    title: '无人驾驶',
    subtitle: 'Autonomous Driving',
    tag: '2025-2032',
    tagColor: '#FF6B35',
    splatColor: 'rgba(255,107,53,0.12)',
    headline: '方向盘消失的时代，出行即是休息',
    episode: 'EP.09',
    storyTitle: '《最后一次开车》',
    story: '2030年，广州。退休司机老张把自己开了40年的出租车钥匙交给了博物馆。那天，他坐进一辆无人驾驶出租车，车里没有方向盘，只有一块大屏幕播放着他最爱的粤剧。车子平稳地穿越城市，精准地停在目的地门口。老张看着窗外，眼眶有些湿润——不是因为失业，而是因为他终于可以在"开车"的时候，好好欣赏这座城市了。',
    desc: '无人驾驶技术正在从L2辅助驾驶快速迈向L5完全自动驾驶。结合6G超低延迟通信、AI实时决策与高精度地图，无人驾驶将彻底改变城市交通格局，消除90%的交通事故，释放数十亿小时的通勤时间。',
    milestones: [
      { year: '2026', event: 'L4级无人驾驶在全国主要城市商业化运营' },
      { year: '2028', event: 'L5完全自动驾驶技术成熟，方向盘开始消失' },
      { year: '2030', event: '无人驾驶车辆占新车销量超过50%' },
      { year: '2032', event: '城市交通事故率降低90%，交通效率提升3倍' },
    ],
    img: 'https://readdy.ai/api/search-image?query=autonomous%20self-driving%20car%20futuristic%20city%20night%20glowing%20orange%20neon%20AI%20driving%20technology%20dark%20background%20sci-fi%20cinematic%20ultra%20detailed%20artistic&width=700&height=420&seq=autodrive-main-v4-001&orientation=landscape',
    stats: [{ label: '事故减少', value: '90%' }, { label: '效率提升', value: '3x' }, { label: '时间释放', value: '10亿h/年' }],
    videoLabel: '观看：无人驾驶城市测试',
    videoUrl: 'https://www.youtube.com/watch?v=aaOB-ErYq6Y',
    applications: ['城市出行', '货物运输', '快递配送', '公共交通'],
  },
];

const storyEpisodes = [
  { ep: 'EP.01', id: 'bci-city', title: '《意念之城》', tech: '脑机接口', year: '2031', teaser: '当思维成为键盘，当梦境成为办公室……' },
  { ep: 'EP.02', id: 'starlink-last', title: '《最后一公里》', tech: '星链', year: '2027', teaser: '6000颗卫星，让世界上最偏远的角落也不再孤独……' },
  { ep: 'EP.03', id: 'evtol-commute', title: '《空中通勤》', tech: 'eVTOL', year: '2030', teaser: '当城市的第三维度成为日常，地面堵车成为传说……' },
  { ep: 'EP.04', id: 'print3d-house', title: '《打印一座房子》', tech: '3D打印', year: '2029', teaser: '72小时，一栋别墅从数字设计变成真实存在……' },
  { ep: 'EP.05', id: 'digital-city', title: '《数字城市》', tech: '空间计算', year: '2028', teaser: '整座城市成为可交互的数字孪生体……' },
  { ep: 'EP.06', id: 'orbit-wedding', title: '《轨道上的婚礼》', tech: '太空旅游', year: '2033', teaser: '10亿人见证了400公里高空的爱情……' },
  { ep: 'EP.07', id: 'last-programmer', title: '《最后一个程序员》', tech: 'AI爆发', year: '2032', teaser: '一个人，一支AI军队，一个亿万用户的世界……' },
  { ep: 'EP.08', id: 'robot-roommate', title: '《我的机器人室友》', tech: '人形机器人', year: '2031', teaser: '它不需要睡觉，不会生气，永远耐心……' },
  { ep: 'EP.09', id: 'last-drive', title: '《最后一次开车》', tech: '无人驾驶', year: '2030', teaser: '方向盘消失的那天，老张终于看清了这座城市……' },
  { ep: 'EP.10', id: 'quantum-key', title: '《量子密钥》', tech: '量子加密', year: '2029', teaser: '当所有密码都可以被破解，只有量子才能守护秘密……' },
  { ep: 'EP.11', id: 'digital-soul', title: '《数字灵魂》', tech: '意识上传', year: '2038', teaser: '当意识可以被上传，死亡还有意义吗？……' },
  { ep: 'EP.12', id: 'nano-doctor', title: '《纳米医生》', tech: '纳米医疗', year: '2033', teaser: '数十亿个纳米机器人在你的血液中游弋，守护你的生命……' },
  { ep: 'EP.13', id: 'mars-child', title: '《火星孩子》', tech: '火星移民', year: '2045', teaser: '第一个在火星出生的孩子，她的家在哪里？……' },
  { ep: 'EP.14', id: 'ocean-city', title: '《海底城市》', tech: '海洋开发', year: '2040', teaser: '当陆地不够用了，人类开始向海洋要空间……' },
  { ep: 'EP.15', id: 'gene-edit', title: '《基因编辑师》', tech: '基因工程', year: '2035', teaser: '当人类可以编辑自己的基因，进化不再是偶然……' },
  { ep: 'EP.16', id: 'energy-sun', title: '《人造太阳》', tech: '核聚变能源', year: '2032', teaser: '人类追逐了70年的梦想，终于在2032年点燃……' },
  { ep: 'EP.17', id: 'virtual-nation', title: '《虚拟国度》', tech: '元宇宙治理', year: '2036', teaser: '当元宇宙拥有了自己的政府、法律和货币……' },
  { ep: 'EP.18', id: 'time-capsule', title: '《时间胶囊》', tech: '数字永存', year: '2050', teaser: '2050年，一个人决定把自己的一生存入元宇宙……' },
];

// Color map for new episodes
const EP_COLORS: Record<string, string> = {
  'EP.01': '#A855F7', 'EP.02': '#00FFCC', 'EP.03': '#FF6B35', 'EP.04': '#FFD700',
  'EP.05': '#00FF88', 'EP.06': '#FF4081', 'EP.07': '#FFD700', 'EP.08': '#00FFCC',
  'EP.09': '#FF6B35', 'EP.10': '#00FFCC', 'EP.11': '#A855F7', 'EP.12': '#00FF88',
  'EP.13': '#FF6B35', 'EP.14': '#00FFCC', 'EP.15': '#FF6B9D', 'EP.16': '#FFD700',
  'EP.17': '#00FF88', 'EP.18': '#A855F7',
};
const EP_ICONS: Record<string, string> = {
  'EP.01': 'ri-mental-health-line', 'EP.02': 'ri-global-line', 'EP.03': 'ri-flight-takeoff-line',
  'EP.04': 'ri-printer-line', 'EP.05': 'ri-map-2-line', 'EP.06': 'ri-rocket-line',
  'EP.07': 'ri-brain-line', 'EP.08': 'ri-robot-line', 'EP.09': 'ri-car-line',
  'EP.10': 'ri-lock-line', 'EP.11': 'ri-user-heart-line', 'EP.12': 'ri-heart-pulse-line',
  'EP.13': 'ri-planet-line', 'EP.14': 'ri-water-flash-line', 'EP.15': 'ri-dna-line',
  'EP.16': 'ri-sun-line', 'EP.17': 'ri-government-line', 'EP.18': 'ri-time-line',
};

export default function FutureTech() {
  const [active, setActive] = useState('bci');
  const [showStory, setShowStory] = useState(false);
  const current = futureTopics.find((t) => t.id === active)!;
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: selectorRef, isVisible: selectorVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: storyRef, isVisible: storyVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="future-tech" className="relative py-24 md:py-32 overflow-hidden doodle-dots-bg" style={{ background: '#060A14' }}>
      {/* Doodle splat backgrounds */}
      <div className="absolute top-20 right-10 w-80 h-80 rounded-full pointer-events-none doodle-splat" style={{ background: current.splatColor, filter: 'blur(60px)', transition: 'background 0.5s' }} />
      <div className="absolute bottom-20 left-10 w-60 h-60 rounded-full pointer-events-none doodle-splat" style={{ background: 'rgba(255,107,53,0.08)', filter: 'blur(50px)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.03) 0%, transparent 70%)' }} />

      {/* Doodle grid */}
      <div className="absolute inset-0 pointer-events-none doodle-grid-bg opacity-40" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="section-badge mb-6 mx-auto w-fit">
            <i className="ri-flashlight-line" />
            Future Frontiers · 前沿科技连载
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            未来前沿
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#FFD700] to-[#FF6B35]"> 科技</span>
          </h2>
          <p className="text-white/40 text-base max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}>
            九大前沿技术 · 十八个改变世界的故事 · 一部关于未来的连载剧集
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#00FF88]/40" />
            <span className="text-[#00FF88]/60 text-xs" style={{ fontFamily: 'Caveat, cursive' }}>集教育 · 科技预测 · 前沿普及为一体</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#00FF88]/40" />
          </div>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/future-tech"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ background: 'rgba(255,215,0,0.12)', border: '1.5px solid rgba(255,215,0,0.4)', color: '#FFD700' }}
            >
              <i className="ri-flashlight-line" />
              查看全部前沿科技详情
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/story-upload"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ background: 'rgba(0,255,204,0.12)', border: '1.5px solid rgba(0,255,204,0.4)', color: '#00FFCC' }}
            >
              <i className="ri-upload-cloud-line" />
              上传我的故事 · 赚取60%收益
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* Topic Selector — 3x3 grid */}
        <div
          ref={selectorRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 mb-12 transition-all duration-700 delay-150 ${selectorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {futureTopics.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActive(t.id); setShowStory(false); }}
              className={`group flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all duration-300 cursor-pointer doodle-hover-card ${
                active === t.id
                  ? 'border-opacity-60 bg-opacity-10'
                  : 'border-white/8 bg-white/2 hover:border-white/20'
              }`}
              style={active === t.id ? { borderColor: `${t.tagColor}60`, backgroundColor: `${t.tagColor}10` } : {}}
            >
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl doodle-icon-wrap transition-colors duration-300 ${active === t.id ? '' : 'bg-white/5 group-hover:bg-white/10'}`}
                style={active === t.id ? { backgroundColor: `${t.tagColor}20` } : {}}>
                <i className={`${t.icon} text-lg`} style={{ color: active === t.id ? t.tagColor : 'rgba(255,255,255,0.4)' }} />
              </div>
              <p className={`font-bold text-xs text-center leading-tight ${active === t.id ? 'text-white' : 'text-white/50'}`}>{t.title}</p>
              <span className="text-white/20 text-xs" style={{ fontFamily: 'Caveat, cursive' }}>{t.episode}</span>
            </button>
          ))}
        </div>

        {/* Detail Panel */}
        <div className="flex flex-col lg:flex-row gap-8 mb-10">
          {/* Image */}
          <div className="flex-1 relative rounded-2xl overflow-hidden border transition-all duration-500 doodle-card" style={{ minHeight: '380px', borderColor: `${current.tagColor}25` }}>
            <img
              src={current.img}
              alt={current.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060A14]/90 via-[#060A14]/30 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#060A14]/60 via-transparent to-transparent" />

            {/* Episode badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold border" style={{ borderColor: current.tagColor, color: current.tagColor, backgroundColor: `${current.tagColor}15`, fontFamily: 'Caveat, cursive', fontSize: '0.9rem' }}>
                {current.episode}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-black/50 text-white/70 border border-white/10">
                {current.tag}
              </span>
            </div>

            {/* Story teaser button */}
            <div className="absolute bottom-4 left-4 right-4">
              <button
                onClick={() => setShowStory(!showStory)}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-white/15 bg-black/60 backdrop-blur-sm hover:border-white/30 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: `${current.tagColor}20` }}>
                    <i className="ri-book-open-line text-sm" style={{ color: current.tagColor }} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold text-sm" style={{ fontFamily: 'Caveat, cursive' }}>{current.storyTitle}</p>
                    <p className="text-white/40 text-xs">点击阅读故事</p>
                  </div>
                </div>
                <i className={`${showStory ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/40`} />
              </button>

              {/* Story content */}
              {showStory && (
                <div className="mt-2 p-4 rounded-xl border border-white/10 bg-black/80 backdrop-blur-sm">
                  <p className="text-white/80 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
                    {current.story}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col justify-between gap-5">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: current.tagColor, fontFamily: 'Caveat, cursive', fontSize: '0.85rem' }}>{current.subtitle}</p>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>{current.title}</h3>
              <p className="text-base font-semibold text-white/80 mb-3 leading-snug" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}>{current.headline}</p>
              <p className="text-white/50 leading-relaxed text-sm">{current.desc}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {current.stats.map((s) => (
                <div key={s.label} className="text-center p-3 rounded-xl border doodle-card" style={{ borderColor: `${current.tagColor}20`, background: `${current.tagColor}06` }}>
                  <p className="font-black text-lg doodle-number" style={{ color: current.tagColor, fontFamily: 'Permanent Marker, cursive' }}>{s.value}</p>
                  <p className="text-white/40 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Applications */}
            <div>
              <p className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-2">应用场景</p>
              <div className="flex flex-wrap gap-2">
                {current.applications.map((app) => (
                  <span key={app} className="doodle-tag text-xs" style={{ color: current.tagColor, borderColor: `${current.tagColor}40`, backgroundColor: `${current.tagColor}08` }}>
                    {app}
                  </span>
                ))}
              </div>
            </div>

            {/* Milestones */}
            <div className="flex flex-col gap-2">
              <p className="text-white/40 text-xs font-semibold tracking-widest uppercase">技术路线</p>
              {current.milestones.map((m, i) => (
                <div key={m.year} className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full border flex-shrink-0 doodle-timeline-dot" style={{ borderColor: `${current.tagColor}40`, backgroundColor: `${current.tagColor}10` }}>
                      <span className="text-xs font-bold" style={{ color: current.tagColor }}>{i + 1}</span>
                    </div>
                    {i < current.milestones.length - 1 && <div className="w-px h-3 bg-white/10" />}
                  </div>
                  <div className="flex-1 pb-1">
                    <span className="text-xs font-bold mr-2" style={{ color: current.tagColor, fontFamily: 'Orbitron, sans-serif' }}>{m.year}</span>
                    <span className="text-white/55 text-xs">{m.event}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Video Button */}
            <a
              href={current.videoUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-3 px-5 py-3 rounded-full border transition-all duration-300 hover:scale-105 w-fit whitespace-nowrap doodle-hover-card"
              style={{ borderColor: `${current.tagColor}40`, color: current.tagColor, backgroundColor: `${current.tagColor}08` }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ backgroundColor: `${current.tagColor}20` }}>
                <i className="ri-play-fill text-sm" />
              </div>
              <span className="text-sm font-semibold" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>{current.videoLabel}</span>
              <span className="text-xs opacity-50">（待制作）</span>
            </a>
          </div>
        </div>

        {/* Story Episodes Grid - 18 stories */}
        <div
          ref={storyRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 delay-200 ${storyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-white/8" />
            <h3 className="text-white/60 text-sm font-bold tracking-widest uppercase" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
              连载故事集 · 18部科幻巨著 · Story Series
            </h3>
            <div className="h-px flex-1 bg-white/8" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {storyEpisodes.map((ep, idx) => {
              const color = EP_COLORS[ep.ep] || '#00FFCC';
              const icon = EP_ICONS[ep.ep] || 'ri-book-line';
              return (
                <Link
                  key={ep.ep}
                  to={`/story/${ep.id}`}
                  className="group flex items-start gap-4 p-4 rounded-2xl border border-white/8 hover:border-white/20 bg-white/2 hover:bg-white/4 transition-all duration-300 text-left cursor-pointer doodle-hover-card"
                  style={{ transitionDelay: `${idx * 30}ms` }}
                >
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl doodle-icon-wrap" style={{ backgroundColor: `${color}15`, border: `1.5px solid ${color}30` }}>
                    <i className={`${icon} text-lg`} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold" style={{ color, fontFamily: 'Caveat, cursive' }}>{ep.ep}</span>
                      <span className="text-white/20 text-xs">{ep.year}</span>
                      <span className="text-white/20 text-xs">·</span>
                      <span className="text-white/30 text-xs">{ep.tech}</span>
                    </div>
                    <p className="text-white/80 font-bold text-sm mb-1" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>{ep.title}</p>
                    <p className="text-white/35 text-xs leading-relaxed line-clamp-2">{ep.teaser}</p>
                  </div>
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                    <i className="ri-arrow-right-line text-white/30 text-xs" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Upload CTA */}
          <div className="mt-8 p-6 rounded-2xl border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ background: 'linear-gradient(135deg, rgba(0,255,204,0.04), rgba(255,215,0,0.03))' }}>
            <div>
              <h4 className="text-white font-bold text-base mb-1">你也有故事想说？</h4>
              <p className="text-white/40 text-sm">上传你的科幻故事，与全球读者分享，作者可获得总收入的 <span className="text-[#FFD700] font-bold">60%</span></p>
            </div>
            <Link to="/story-upload" className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all hover:scale-105 whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #FFD700, #FF6B35)', color: '#000' }}>
              <i className="ri-upload-cloud-line" />上传我的故事
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 p-8 md:p-10 doodle-card"
            style={{ background: 'linear-gradient(135deg, rgba(0,255,136,0.04) 0%, rgba(255,215,0,0.03) 50%, rgba(255,107,53,0.04) 100%)' }}>
            <div className="absolute inset-0 doodle-grid-bg opacity-30 pointer-events-none" />
            <div className="absolute top-4 right-8 w-20 h-20 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%)' }} />
            <div className="absolute bottom-4 left-8 w-16 h-16 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,80,150,0.12) 0%, transparent 70%)' }} />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  加入 META66，<span className="text-[#00FF88]">共创数字文明</span>
                </h3>
                <p className="text-white/50 text-sm leading-relaxed max-w-xl">
                  十八大前沿技术，十八个改变世界的故事，正在真实发生。META66为你提供最前沿的资讯、工具与生态资源，成为数字文明新纪元的先行者。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <a
                  href="#contact"
                  onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00FF88] text-black font-bold text-sm hover:bg-[#00FFCC] transition-all duration-300 hover:scale-105 whitespace-nowrap btn-doodle"
                >
                  <span>立即加入</span>
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href="#training"
                  onClick={(e) => { e.preventDefault(); document.querySelector('#training')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[#FFD700]/40 text-[#FFD700] font-semibold text-sm hover:bg-[#FFD700]/10 transition-all duration-300 whitespace-nowrap"
                >
                  <span>查看培训课程</span>
                  <i className="ri-book-open-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
